import type { LayoutItem } from 'react-grid-layout'

import type { WidgetSizeDto } from '@pages/IntroducingBroker/DashboardPoC/dashboardApi.types'
import type { Widget, WidgetLayout } from '@pages/IntroducingBroker/DashboardPoC/types'
import {
  DASHBOARD_MOBILE_LAYOUT_COLUMNS,
  DASHBOARD_WIDGET_GAP_ROWS,
} from '@pages/IntroducingBroker/DashboardPoC/utils/dashboardGrid.constants'

interface WidgetGridDimensions {
  w: number
  h: number
}

function clampWidgetSize(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max))
}

function buildLayoutConstraints(size: WidgetSizeDto) {
  return {
    minW: size.minWidth,
    minH: size.minHeight,
    maxW: size.maxWidth,
    maxH: size.maxHeight,
  }
}

export function getDefaultGridSize(size: WidgetSizeDto): WidgetGridDimensions {
  return {
    w: size.defaultWidth,
    h: size.defaultHeight,
  }
}

export function getWidgetConstraints(
  newLayout: LayoutItem,
  widgetId: string,
  baseWidgetLayout: WidgetLayout | null,
  size: WidgetSizeDto
): WidgetLayout {
  const { minW, minH, maxW, maxH } = buildLayoutConstraints(size)

  return {
    ...(baseWidgetLayout ?? {}),
    i: widgetId,
    x: newLayout.x,
    y: newLayout.y,
    w: clampWidgetSize(newLayout.w, minW, maxW),
    h: clampWidgetSize(newLayout.h, minH, maxH),
    minW,
    minH,
    maxW,
    maxH,
  }
}

export function arrangeMobileWidgets(widgets: Widget[]): Widget[] {
  const mobileLayouts = new Map<string, WidgetLayout>()
  const mobileWidgets = widgets
    .map((widget, index) => ({ widget, index }))
    .filter(({ widget }) => widget.mobileLayout != null || widget.inUse?.mobile === true)
    .sort((a, b) => {
      const aHasLayout = a.widget.mobileLayout != null
      const bHasLayout = b.widget.mobileLayout != null

      if (aHasLayout !== bHasLayout) {
        return aHasLayout ? -1 : 1
      }

      return (
        (a.widget.mobileLayout?.y ?? 0) - (b.widget.mobileLayout?.y ?? 0) ||
        (a.widget.mobileLayout?.x ?? 0) - (b.widget.mobileLayout?.x ?? 0) ||
        a.index - b.index
      )
    })

  let y = 0

  mobileWidgets.forEach(({ widget }) => {
    const height = widget.size.minHeight

    mobileLayouts.set(widget.id, {
      i: widget.id,
      x: 0,
      y,
      w: DASHBOARD_MOBILE_LAYOUT_COLUMNS,
      h: height,
      minW: DASHBOARD_MOBILE_LAYOUT_COLUMNS,
      minH: height,
      maxW: DASHBOARD_MOBILE_LAYOUT_COLUMNS,
      maxH: height,
    })

    y += height + DASHBOARD_WIDGET_GAP_ROWS
  })

  return widgets.map((widget) => ({
    ...widget,
    mobileLayout: mobileLayouts.get(widget.id) ?? widget.mobileLayout,
  }))
}
